import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import SOURCES from "../../../../utils/constants";
import LocalStorageService from "../../../../utils/localStorageServices";
import {
    formatDateTime
} from "../../../../utils/functions";
import { MSG_PROFILE } from "../../../../utils/message";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { useQueryClient } from "@tanstack/react-query";
import { message, Form } from "antd";
import CommonForm from "../../../global/form";

const localStorageServices = LocalStorageService.getService();

const EnhancedProfileDetails = forwardRef(({ onSuccess, userData, isLoading }, ref) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const currentUser = localStorageServices.getCurrentUser();
    const userRole = currentUser?.user?.userRole;

    const [timezoneList, setTimezoneList] = useState([]);

    const saveProfile = useGenericMutation({
        endpoint: `user/profile`,
        method: "PUT",
        onSuccess: () => {
            message.success(MSG_PROFILE.UPDATE);
            queryClient.invalidateQueries({ queryKey: ["user", "detail", "me", currentUser.user.email] });
            if (onSuccess) onSuccess();
        },

    });

    const { data: timezones } = useGenericQuery({
        endpoint: `common/timezones`,
        queryKey: ["common", "timezones"]
    });

    useEffect(() => {
        if (userData) {
            const user = userData;

            form.setFieldsValue({
                email: user.email,
                name: user.name,
                phone: user.phone,
                company: user.company,
                timezone: user.timezone?.name,
                registeredAt: formatDateTime(user.createdAt)
            });
        }
    }, [userData, form]);

    useEffect(() => {
        if (timezones) {
            const tzList = timezones.map((tz) => ({
                label: `${tz.text} ${tz.utc[0]}`,
                value: tz.utc[0],
                offset: tz.offset
            }));
            setTimezoneList(tzList);
        }
    }, [timezones]);


    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const selectedTzObj = timezoneList.find(tz => tz.value === values.timezone);

            let offset = -7;
            if (selectedTzObj) {
                offset = selectedTzObj.offset;
            } else if (timezones) {
                const tz = timezones.find(t => t.utc[0] === values.timezone);
                if (tz) offset = tz.offset;
            }

            return await saveProfile.mutateAsync(
                {
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    company: values.company,
                    timezone: { name: values.timezone, offset: offset },
                }
            );
        } catch (e) {
            console.error("Validation Failed:", e);
            throw e;
        }
    };

    // Expose handleSubmit to parent via ref
    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
        isPending: saveProfile.isPending
    }));




    const formData = [
        {
            label: "Email",
            name: "email",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Email",
            disabled: true,
            subTitle: userRole !== SOURCES.USER_ROLE_ADMIN ? "To update your email address, please contact support." : "",
            span: 24
        },
        {
            label: "Name",
            name: "name",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Name",
            required: true,
            message: "Name is required.",
            span: 24
        },
        {
            label: "Phone",
            name: "phone",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Phone number",
            span: 12
        },
        {
            label: "Company",
            name: "company",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            placeholder: "Company name",
            span: 12
        },
        {
            label: "Timezone",
            name: "timezone",
            inputType: SOURCES.FORMINPUTTYPES.SELECT,
            placeholder: "Select Timezone",
            options: timezoneList,
            span: 12,
        },
        {
            label: "Registered At",
            name: "registeredAt",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            type: "text",
            disabled: true,
            span: 12
        }
    ];


    return (
        <div className="w-full">
            <CommonForm
                data={formData}
                form={form}
                className="profile-form mt-4"
            />
        </div>
    );
});

export default EnhancedProfileDetails;
